<template>
  <div class="m-select" :style="{ width: width ?? '' }" ref="selfRef">
    <div v-if="filterable" class="filterable">
      <MInput
        :placeholder="props.placeholder ?? `<${$t('none')}>`"
        type="text"
        class="m-select-toggle"
        v-model="selectedValue"
        autocomplete="off"
        ref="selectToggle"
        :popovertarget="`select-${id}`"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @click="onClick"
        @keydown.tab.prevent="onTab" />
      <div class="autocomplete">
        <div class="value">{{ selectedValue }}</div>
        <div class="ghost">{{ ghostValue }}</div>
      </div>
      <MButton v-if="selectedValue" design-style="mono" class="toggle" only-icon @click="clearFilterValue"><Icon icon="Close" /></MButton>
      <MButton v-else design-style="mono" class="toggle" only-icon @click="show"><Icon icon="ChevronBottom" /></MButton>
    </div>
    <MButton
      v-else
      design-style="select"
      class="m-select-toggle"
      :class="{ active: vm.isOpen, error: !selectedEntry && !placeholder }"
      ref="selectToggle"
      :popovertarget="`select-${id}`"
      data-cy="select-condition-button">
      <div class="toggle-button-content">
        <span class="select-toggle-text" v-if="selectedEntry">
          <slot :selectedItem="selectedEntry" :selectedText="getEntryText(selectedEntry, translateTextField)" name="button-content">
            {{ getEntryText(selectedEntry, translateTextField) }}
          </slot>
        </span>
        <div v-else class="select-toggle-placeholder-text">
          {{ placeholder ?? `<${$t("none")}>` }}
        </div>
        <div class="select-toggle-caret">
          <Icon icon="ChevronBottom" />
        </div>
      </div>
    </MButton>
    <div
      :id="`select-${id}`"
      class="m-select-menu"
      :class="{ 'is-open': vm.isOpen }"
      popover
      ref="selectMenu"
      @toggle="setState"
      :style="{
        transform: `translate(${vm.menuPositionX}px, ${vm.menuPositionY}px)`,
        minWidth: `${vm.selectToggleWidth}px`
      }">
      <slot v-if="!vm.validationFeedback" :items="filterable ? filteredItems : consolidatedItems">
        <template v-for="(entry, index) in filterable ? filteredItems : consolidatedItems" :key="getEntryKey(entry)">
          <slot v-if="'groupName' in entry && entry.groupName && checkGroupName(index, entry)" :entry="entry" :groupName="groupName" name="item-template"
            ><h6 class="p-2 mb-0">{{ entry.groupName }}</h6></slot
          >

          <div
            :data-cy="'select-menu-entry-' + getEntryText(entry)"
            class="select-menu-entry"
            :class="{
              active: valueField ? entry[valueField] == selectedEntry?.[valueField] : entry == selectedEntry,
              suggest: entry === suggestedEntry,
              disabled: !_.isString(entry) ? entry?.disabled : undefined,
              'separator-before': !_.isString(entry) ? entry?.separator === 'top' : undefined,
              'separator-after': !_.isString(entry) ? entry?.separator === 'bottom' : undefined,
              'pl-3': entry.groupName
            }"
            @click="entryClickedHandler(entry)">
            <slot :entry="entry" name="item-template">{{ getEntryText(entry, translateTextField) }}</slot>
          </div>
        </template>
      </slot>
      <EmptyState v-else is-insde-element>
        {{ vm.validationFeedback }}
      </EmptyState>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from "@common/components/Icon.vue";
import { Guid } from "@util/guid";
import { useScroll } from "@vueuse/core";
import _ from "lodash";
import { Ref, WatchStopHandle, computed, nextTick, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import MButton from "./MButton.vue";
import MInput from "./MInput.vue";
import EmptyState from "@common/components/EmptyState.vue";

//#region Refs
const selfRef = ref<HTMLElement | null>(null);
const selectMenu = ref<HTMLElement | null>(null);
const selectToggle = ref<typeof MButton | typeof MInput | null>(null);
let scrollParent: Ref<HTMLElement> | null;

const selectedValue = ref<string | null>(null);
const activeIndex = ref(0);
//#endregion

//#region General
const id = Guid.new();

const { t: $t } = useI18n();

const props = withDefaults(
  defineProps<{
    value?: any | null;
    items: any[] | Record<string, any[]>;
    placeholder?: string;
    width?: string;
    textField?: string;
    valueField?: string;
    translateTextField?: boolean;
    filterable?: boolean;
  }>(),
  { textField: "name" }
);
const emits = defineEmits(["input", "selection", "cleared"]);
//#endregion

//#region Viewmodel
type ViewModel = { isOpen: boolean; menuPositionX: number; menuPositionY: number; selectToggleWidth: number; validationFeedback: string };
const vm = reactive<ViewModel>({ isOpen: false, menuPositionX: 0, menuPositionY: 0, selectToggleWidth: 0, validationFeedback: "" });
//#endregion

//#region Helpers
const getEntryKey = (entry: any): string | number | symbol | undefined => {
  if (typeof entry == "string") return entry;
  const keyValue = props.valueField ? entry[props.valueField] : entry[props.textField];
  if (typeof keyValue === "string" || typeof keyValue === "number" || typeof keyValue === "symbol" || typeof keyValue === "undefined") return keyValue;
};
const getEntryText = (entry: any, getTranslated: boolean = false) => {
  if (typeof entry == "string") return entry;
  const textFieldValue: any = entry?.[props.textField];
  if (!entry || !textFieldValue) return "";
  const result = getTranslated ? $t(textFieldValue) : textFieldValue;
  return result;
};
//#endregion

//#region SelectedEntry
const consolidatedItems = computed(() => (_.isArray(props.items) ? props.items : flatObject(props.items)));

const selectedEntry = computed({
  get: () => {
    return consolidatedItems.value.find((x) => (props.valueField ? x[props.valueField] == props.value : x == props.value)) ?? null;
  },
  set: (nv: any) => {
    emits("input", props.valueField ? nv[props.valueField] : nv);
  }
});

const entryClickedHandler = (entry: any) => {
  if (entry?.disabled) return;
  selectedEntry.value = entry;

  emits("selection", entry);
  hide();
};
//#endregion

//#region ScrollHandle
let isScrollingWatcher: WatchStopHandle | null = null;

const enableCloseOnScroll = () => {
  const foundParent = getScrollParent(selfRef.value);
  scrollParent = foundParent ? ref(foundParent) : null;
  if (scrollParent?.value) {
    const { isScrolling } = useScroll(scrollParent);
    isScrollingWatcher = watch(isScrolling, () => {
      hide();
    });
  }
};

function getScrollParent(node: HTMLElement | ParentNode | null) {
  if (node == null) return null;
  if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight) {
    const computedStyle = getComputedStyle(node);
    if (
      computedStyle.overflow == "auto" ||
      computedStyle.overflowY == "auto" ||
      computedStyle.overflowX == "auto" ||
      computedStyle.overflow == "scroll" ||
      computedStyle.overflowY == "scroll" ||
      computedStyle.overflowX == "scroll"
    )
      return node;
    else return getScrollParent(node.parentNode);
  } else {
    return getScrollParent(node.parentNode);
  }
}
//#endregion

//#region Filterable
const filteredItems = computed(() => {
  if (!Array.isArray(props.items)) return [];
  if (!selectedValue.value) return props.items;
  return props.items.filter((item) => getEntryText(item).toLowerCase().startsWith(selectedValue.value!.toLowerCase()));
});

const suggestedEntry = computed(() => {
  if (selectedValue.value === "") return null;
  const item = filteredItems.value[activeIndex.value] || null;
  return item;
});

const ghostValue = computed(() => {
  if (!selectedValue.value || !suggestedEntry.value) return "";
  const entryText = getEntryText(suggestedEntry.value);
  const prefixLen = commonPrefixLength(selectedValue.value, entryText);
  return entryText.slice(prefixLen);
});

function commonPrefixLength(a: string, b: string) {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i].toLowerCase() !== b[i].toLowerCase()) {
      return i;
    }
  }
  return len;
}

const clearFilterValue = () => {
  selectedValue.value = "";
  emits("cleared");
  const inputEl = selectToggle.value?.$el instanceof HTMLInputElement ? selectToggle.value.$el : null;
  inputEl?.focus();
};

const onArrowUp = () => {
  if (!vm.isOpen) show();
  if (activeIndex.value == 0) {
    activeIndex.value = filteredItems.value.length - 1;
  } else {
    activeIndex.value--;
  }
  nextTick(() => {
    selectMenu.value?.querySelector(".select-menu-entry.suggest")?.scrollIntoView(false);
  });
};

const onArrowDown = () => {
  if (!vm.isOpen) show();
  if (activeIndex.value === filteredItems.value.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
  nextTick(() => {
    selectMenu.value?.querySelector(".select-menu-entry.suggest")?.scrollIntoView(true);
  });
};

const onClick = () => {
  if (filteredItems.value.length && getEntryText(selectedEntry.value) !== selectedValue.value && !vm.isOpen) show();
};

const onEnter = () => {
  if (suggestedEntry.value) {
    selectedValue.value = getEntryText(suggestedEntry.value);
    activeIndex.value = 0;
    entryClickedHandler(suggestedEntry.value);
  }
};

const onTab = () => {
  onEnter();
};

watch(
  () => selectedValue.value,
  (nv) => {
    if (!nv) {
      clearFilterValue();
    }
  }
);

watch(
  () => selectedEntry.value,
  () => {
    selectedValue.value = selectedEntry.value ? getEntryText(selectedEntry.value) : "";
    nextTick(() => {
      hide();
    });
  },
  { immediate: true }
);

watch(
  () => suggestedEntry.value,
  (nv) => {
    if (!props.filterable) return;
    if (selectedValue.value && !nv) {
      vm.validationFeedback = $t("noMatch", { value: selectedValue.value });
    } else {
      vm.validationFeedback = "";
    }
    if (nv || vm.validationFeedback) {
      show();
    }
  },
  { deep: true }
);

watch(
  () => filteredItems.value.length,
  (nv, ov) => {
    if (nv !== ov) {
      calculatePositionOfMenu();
    }
  }
);

function flatObject(object: Record<string, any[]>) {
  const result = [];
  for (const groupName in object) {
    const group = object[groupName];

    if (Array.isArray(group)) {
      for (const item of group) {
        result.push({
          ...item,
          groupName
        });
      }
    }
  }
  return result;
}

function checkGroupName(entryIndex: number, entry: any) {
  if (entryIndex === 0) return true;
  const previousEntry = consolidatedItems.value[entryIndex - 1];
  return previousEntry.groupName !== entry.groupName;
}
//#endregion

const show = () => {
  if (selectMenu.value) {
    selectMenu.value.showPopover();
  }
};

const hide = () => {
  if (selectMenu.value) {
    activeIndex.value = 0;
    selectMenu.value.hidePopover();
  }
};
const toggle = () => {
  if (selectMenu.value) {
    selectMenu.value.togglePopover(!vm.isOpen);
  }
};

const setState = () => {
  if (selectMenu.value) {
    vm.isOpen = selectMenu.value.matches(":popover-open");
    if (vm.isOpen) {
      calculatePositionOfMenu();
      enableCloseOnScroll();
    } else if (isScrollingWatcher) {
      isScrollingWatcher();
      isScrollingWatcher = null;
    }
  }
};

const calculatePositionOfMenu = () => {
  vm.menuPositionX = 0;
  vm.menuPositionY = 0;
  const selectToggleRect =
    selectToggle.value instanceof HTMLInputElement ? selectToggle.value.getBoundingClientRect() : selectToggle.value?.$el?.getBoundingClientRect();
  vm.selectToggleWidth = selectToggleRect ? selectToggleRect.width : 0;

  nextTick(() => {
    if (selectMenu.value && selectToggle.value) {
      const menuRect = selectMenu.value.getBoundingClientRect();
      let x = 0;
      let y = 0;

      if (selectToggleRect && menuRect) {
        x = selectToggleRect.x - menuRect.x;
        y = selectToggleRect.y - menuRect.y + selectToggleRect.height;
        if (menuRect.bottom + y > window.innerHeight) {
          y = selectToggleRect.y - menuRect.bottom;
        }
      }
      vm.menuPositionX = x;
      vm.menuPositionY = y;
    }
  });
};

defineExpose({ show, hide, toggle });
</script>
